import { useEffect, useState } from 'react';
import axios from 'axios';
import { BaseAPI } from 'src/providers/base-api';
import { getStoredTenant } from 'src/utils/tenantUtils';
import { useAuth } from 'src/hooks/useAuth';
import SuspenseLoader from 'src/components/SuspenseLoader';

/**
 * SSO auto-redirect gate for the local login pages ('/', '/login', '/login-old').
 *
 * In a crelo-platform SSO deployment, an unauthenticated visitor should never
 * see HRIS's own local login form — they should be sent straight to the
 * platform login. This component checks the flag (via the public, no-auth
 * `GET /public/sso-config` endpoint) once per mount and either:
 *   - leaves the SPA entirely via `window.location.assign(...)` to the
 *     backend's `/sso/login` route (which 302s on to the platform), or
 *   - renders `children` (the existing local login page) unchanged when SSO
 *     is disabled, the request fails, or the user is already authenticated.
 *
 * Flag-off / disabled behavior is byte-identical to today: the config fetch
 * still happens, but `enabled` is false, so `children` renders exactly as it
 * did before this component existed.
 *
 * Because the redirect target is the backend origin (not an SPA route), there
 * is no risk of an SPA-internal redirect loop — the browser navigates away
 * before any further React rendering/routing occurs.
 */
interface SSOAutoRedirectProps {
  children: React.ReactNode;
}

const DEFAULT_TENANT_SLUG = 'cloudripples';

export default function SSOAutoRedirect({ children }: SSOAutoRedirectProps) {
  const { isAuthenticated } = useAuth();

  // 'checking' (fetching sso-config) | 'redirecting' (leaving the SPA) | 'local' (render children)
  const [phase, setPhase] = useState<'checking' | 'redirecting' | 'local'>(
    isAuthenticated ? 'local' : 'checking'
  );

  useEffect(() => {
    // Authenticated users must never be redirected away — render local content.
    if (isAuthenticated) {
      setPhase('local');
      return;
    }

    let cancelled = false;
    setPhase('checking');

    // A timeout is NOT optional here. This component gates the login page:
    // until the request settles, `phase` stays 'checking' and the user sees a
    // SuspenseLoader instead of the form. axios defaults to `timeout: 0` —
    // wait forever — so a backend that accepts the connection and then stalls
    // (rather than refusing it) locks every user out of logging in, with no
    // error for the .catch() below to fail safe on. Three seconds is well
    // above the round-trip for what is a static flag read, and a timeout
    // rejects into the same fail-safe path as any other failure.
    axios
      .get(BaseAPI.buildAPIURL('/public/sso-config'), { timeout: 3000 })
      .then((response) => {
        if (cancelled) return;

        if (response?.data?.enabled === true) {
          setPhase('redirecting');
          const tenantSlug = getStoredTenant() || DEFAULT_TENANT_SLUG;
          const loginPath = response.data.loginPath || '/sso/login';
          // Leaves the SPA entirely — navigates to the backend origin, which
          // 302s on to the platform. No SPA-internal loop is possible.
          window.location.assign(
            `${BaseAPI.buildAPIURL(loginPath)}?tenant=${encodeURIComponent(tenantSlug)}`
          );
        } else {
          setPhase('local');
        }
      })
      .catch(() => {
        // SSO config unavailable, or the request timed out — fail safe to the
        // local login form, unchanged from today.
        if (!cancelled) {
          setPhase('local');
        }
      });

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated]);

  if (phase === 'checking' || phase === 'redirecting') {
    return <SuspenseLoader />;
  }

  return <>{children}</>;
}
